import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../generic-components/Heading';
import { useStore } from '../../../../store';
import { IAdditionalBusiness } from '../switch-trigger/ProfilesRender';
import { PROFILE_STYLES } from '../../constants';
import Button from '../../../../generic-components/Button';
import { ReactComponent as AccountActiveIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-selected.svg';
import { ReactComponent as AccountInactiveIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-unselected.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/svg/x-icon-v2.svg';
import { ReactComponent as AddBusinessIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-add.svg';
import { ReactComponent as ResumeIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-resume.svg';
import { ReactComponent as LiliIcon } from '../../../../assets/icons/svg/icon-new-lili-logo.svg';
import { ReactComponent as SwitcheLoaderRight } from '../../../../assets/account-switcher/active-switch-right.svg';
import { ReactComponent as SwitcheLoaderLeft } from '../../../../assets/account-switcher/active-switch-left.svg';
import { removeFromSessionStorage, setToSessionStorage } from '../../../../services/storage.service';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import { navigateToContinueWhereYouLeft, VIEWS_MAP } from '../../../../router/constants';
import { ResetSlicesForAccountSwitching } from '../../../../store/service';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { SWITCHER_STYLES } from './constants';
import { validateFeatureFlag } from '../../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../../constants/general';
import { InitialsCircle } from '../../../../components/InitialsCircle';
import { IBusiness } from '../../../../types';
import { eventBus } from '../../../../services/eventBus';
import { Avatar, AvatarSize } from '../../../../DesignSystem/VendorProfile/Avatar';
import { DASHBOARD_MODAL_SHOWN_KEY } from '../../../../services/useGlobalModalsManager';

interface IProps {
  onClose: () => void;
}

export const AccountSwitcher = ({ onClose }: IProps) => {
  const { otherBusinesses, business, dba, customer, actionFlags } = useStore((state) => state.summary);
  const { customerId } = useStore((state) => state.user);
  const personExternalId = useStore((state) => state.getPersonExternalId());
  const resetUserSliceId = useStore((state) => state.resetUserSliceId);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refetch: fetchSummaryData } = useAccountSummaryQuery(() => {
    navigate(`/${VIEWS_MAP.GLOBAL_LOADER}`);
    setTimeout(() => {
      navigate(`/${VIEWS_MAP.DASHBOARD}`, { replace: true });
      eventBus.emit('closeProfileMenu', '');
      onClose();
    }, 0);
  }, selectedAccountId);
  const handleAccountStrip = (id: string, isActive: boolean) => {
    if (isActive) return;
    /** Update SessionStorage with new customerId */
    setToSessionStorage({ key: 'customerId', value: id });
    /** Reset logged-in user id */
    resetUserSliceId(id);
    /** Reset needed slices */
    ResetSlicesForAccountSwitching();
    /** Set the new selected account id */
    setSelectedAccountId(id);
    removeFromSessionStorage(DASHBOARD_MODAL_SHOWN_KEY);
  };

  const selectedBusiness: IAdditionalBusiness | undefined = useMemo(
    () => otherBusinesses?.filter((b: IAdditionalBusiness) => b.customerExternalId === selectedAccountId)[0],
    [selectedAccountId],
  );

  useEffect(() => {
    if (selectedAccountId) {
      setTimeout(() => {
        fetchSummaryData();
      }, 1000);
    }
  }, [selectedAccountId]);

  const renderAccountStrip = (
    currentBusiness: IBusiness,
    name: string,
    id: string,
    isActive: boolean,
    ein?: string,
  ) => (
    <Button
      onClick={() => handleAccountStrip(id, isActive)}
      className={SWITCHER_STYLES.ACCOUNT_STRIP_BUTTON}
      id={`business-${id}`}
      disable={isActive}
      childElement={
        <div className={SWITCHER_STYLES.CHILDREN_CONTAINER}>
          <div className={SWITCHER_STYLES.CHILDREN_LEFT_CONTAINER}>
            <Avatar
              name={currentBusiness.name || `${customer.firstName} ${customer.lastName}`}
              id={id}
              size={AvatarSize.LARGE}
            />
            <div className={SWITCHER_STYLES.FLEX_COL_START}>
              <Heading title={name} className={SWITCHER_STYLES.ACCOUNT_NAME} />
              {ein && <Heading title={ein} className={SWITCHER_STYLES.ACCOUNT_EIN} />}
            </div>
          </div>
          {isActive ? <AccountActiveIcon /> : <AccountInactiveIcon />}
        </div>
      }
    />
  );

  const handleAddNewBusiness = () => {
    const isInteg = window.location.host.includes('integ') || window.location.host.includes('localhost');
    const isSandbox = window.location.host.includes('sandbox');

    if (isSandbox) {
      window.location.replace(`https://sandbox.lili.co/multi/intro?id=${personExternalId}`);
    } else if (isInteg) {
      window.location.replace(`https://integ.lili.co/multi/intro?id=${personExternalId}`);
    } else {
      window.location.replace(`https://prod.lili.co/multi/intro?id=${personExternalId}`);
    }
  };

  const handleResumeOb = () => {
    navigateToContinueWhereYouLeft(customer.email);
  };

  return (
    <div className={SWITCHER_STYLES.MODAL_CONTAINER}>
      {selectedBusiness && (
        <UserMessage
          mainWrapperOverrideClassName={SWITCHER_STYLES.LOADER_MODAL_WRAPPER}
          onClick={() => {}}
          id='swithcerLoader'
        >
          <div className='flex flex-col items-center'>
            <div className='mb-[20px]'>
              <InitialsCircle
                business={selectedBusiness as IBusiness}
                circleWrapperClassName={SWITCHER_STYLES.LOADER_PROFILE}
                initialsClassName={PROFILE_STYLES.ACCOUNT_SWITCHER_TRIGGER.PROFILES.PROFILE_ITEM_TITLE}
              >
                <div className='absolute inset-0 animate-spin'>
                  <SwitcheLoaderRight className={SWITCHER_STYLES.LOADER_ICON_RIGHT} />
                  <SwitcheLoaderLeft className={SWITCHER_STYLES.LOADER_ICON_LEFT} />
                </div>
              </InitialsCircle>
            </div>

            <Heading
              title={`${t('ACCOUNT_SWITCHER.LOADING_MESSAGE')} ${selectedBusiness.name}...`}
              className={SWITCHER_STYLES.LOADER_TITLE}
            />
            <LiliIcon className={SWITCHER_STYLES.LOADER_LILI_ICON} />
          </div>
        </UserMessage>
      )}
      <div className={SWITCHER_STYLES.HEADER_WRAPPER}>
        <Heading title={t('ACCOUNT_SWITCHER.MODAL_TITLE')} className={SWITCHER_STYLES.HEADER_TITLE} />
        <Button
          onClick={onClose}
          className=''
          id='closeAccountSwitcher'
          childElement={<CloseIcon className='w-[20px]' />}
        />
      </div>
      <div className={SWITCHER_STYLES.BODY_WRAPPER}>
        {renderAccountStrip(
          business as IBusiness,
          business?.name || dba?.name || `${customer?.firstName} ${customer?.lastName}`,
          customerId,
          true,
          business?.ein || customer?.ssn || customer.idNumber,
        )}
        {otherBusinesses?.map((b: IAdditionalBusiness) => (
          <React.Fragment key={b.customerExternalId}>
            {renderAccountStrip(
              b as IBusiness,
              b.name,
              b.customerExternalId,
              false,
              b?.ein || customer?.ssn || customer.idNumber,
            )}
          </React.Fragment>
        ))}
      </div>
      {actionFlags?.businessInProgress && validateFeatureFlag(FEATURE_FLAG.ADD_NEW_BUSINESS) && (
        <div className={SWITCHER_STYLES.FOOTER_WRAPPER}>
          <Button
            onClick={handleResumeOb}
            className='w-full'
            testId='AccountSwitcher__resume-ongoing-application'
            id=''
            childElement={
              <div className={SWITCHER_STYLES.FOOTER_BODY}>
                <ResumeIcon className='w-[40px]' />
                <Heading title={t('ACCOUNT_SWITCHER.RESUME_OB')} className={SWITCHER_STYLES.FOOTER_TITLE} />
              </div>
            }
          />
        </div>
      )}
      {actionFlags?.allowAdditionalBusiness &&
        !actionFlags?.businessInProgress &&
        validateFeatureFlag(FEATURE_FLAG.ADD_NEW_BUSINESS) && (
          <div className={SWITCHER_STYLES.FOOTER_WRAPPER}>
            <Button
              onClick={handleAddNewBusiness}
              className='w-full'
              testId='AccountSwitcher__add_new_business'
              id='AccountSwitcher__add_new_business'
              childElement={
                <div className={SWITCHER_STYLES.FOOTER_BODY}>
                  <AddBusinessIcon className='w-[40px]' />
                  <Heading
                    id='action_title'
                    title={t('ACCOUNT_SWITCHER.ZERO_STATE')}
                    className={SWITCHER_STYLES.FOOTER_TITLE}
                  />
                </div>
              }
            />
          </div>
      )}
    </div>
  );
};
